import { noteCount } from '../editor/pageFormat';
import type { fabric } from 'fabric';
import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { exportFileName } from '../export/plan';
import {
  checkLimitKb, checkMult, DEFAULT_SETTINGS, JPG_QUALITY_LABEL, parseSettings, PNG_QUALITY_LABEL, SETTINGS_KEY, sizePlan,
  type ExportSettings
} from '../export/options';
import { exportDesign, type ExportResult, type Format, type PictureOptions } from '../export/render';
import { formatRange, parseRange, pickPages, uniqueNames, type PagesMode } from '../export/pagePick';
import { pageThumb, readyThumb } from './pageThumbs';
import { DOC_PPI, objectBounds, preflight, preflightDoc, pxToMm, mmToPx, type Check, type PlainObject } from '../print/rules';
import { api, type PrintProfileInfo } from '../sync/api';
import { thStamp } from '../time';
import { $, setPressed } from './dom';
import { closeSheets } from './sheets';
import { toast } from './toast';

/*
 * "ส่งออก": PNG, JPG, PDF ภาพ (one picture per page, RGB), PDF เอกสาร (RGB vector, copyable text, small)
 * and PDF โรงพิมพ์ (CMYK vector, bleed, crop marks) — the last two in src/print/pdf.ts.
 * Both vector PDFs show a "ตรวจก่อนส่ง" list first; nothing in it blocks the download, and its buttons fix
 * the design in place (only on your own work).
 * PNG/JPG: ขนาด (×0.25–×3), คุณภาพ, พื้นหลังโปร่งใส (PNG); "บันทึกการตั้งค่าการดาวน์โหลด" keeps them in this
 * device's browser (src/export/options.ts).
 */

type Choice = Format | 'doc' | 'print';
const EXT: Record<Choice, string> = { png: 'png', jpg: 'jpg', pdf: 'pdf', doc: 'pdf', print: 'pdf' };
/** the two vector PDFs share the info box and the check list */
const isVector = (c: Choice): c is 'doc' | 'print' => c === 'doc' || c === 'print';

/** Hand the file to the browser as a download (works on iPadOS/iOS Safari 13+). */
/** save a file made here (also used by the object menu's "ดาวน์โหลดที่เลือก") */
export function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** file size for people: KB under 1 MB (a size-limited JPG of 40 KB is not "0.0 MB") */
const size = (n: number) => n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024)).toLocaleString('en-US')} KB`
  : `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
const errText = (e: unknown) => e instanceof Error && e.message ? e.message : 'ไม่ทราบสาเหตุ';
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

/** ICC bytes of a profile, downloaded once per page load */
const iccCache = new Map<string, Promise<Uint8Array>>();
function iccOf(p: PrintProfileInfo): Promise<Uint8Array> {
  let v = iccCache.get(p.id + p.url);
  if (!v) {
    v = (async () => {
      let res: Response;
      try { res = await fetch(p.url, { credentials: 'same-origin' }); }
      catch { throw new Error('โหลดไฟล์โปรไฟล์สีไม่ได้ (ไม่มีอินเทอร์เน็ต?)'); }
      if (!res.ok) throw new Error(`โหลดไฟล์โปรไฟล์สี "${p.name}" ไม่ได้ (เซิร์ฟเวอร์ตอบ ${res.status})`);
      const b = new Uint8Array(await res.arrayBuffer());
      if (b.length < 132 || String.fromCharCode(...b.subarray(36, 40)) !== 'acsp') throw new Error(`ไฟล์โปรไฟล์สี "${p.name}" ที่ได้มาไม่ใช่ไฟล์ ICC`);
      return b;
    })();
    v.catch(() => iccCache.delete(p.id + p.url));
    iccCache.set(p.id + p.url, v);
  }
  return v;
}

/** `setBleed`: give the listed pages (0 = page 1) this bleed — the check list's "ตั้งระยะตัดตก" */
export function initExportSheet(ed: Editor, history: History, opts: { canEdit: () => boolean; setBleed: (mm: number, pages: number[]) => void }) {
  /* ---------- the PNG/JPG options, and remembering them ---------- */
  let remember = false;
  let set: ExportSettings = { ...DEFAULT_SETTINGS };
  {
    let raw: string | null = null, readErr = '';
    try { raw = localStorage.getItem(SETTINGS_KEY); } catch (e) { readErr = errText(e); }
    if (readErr) toast(`อ่านการตั้งค่าการดาวน์โหลดที่บันทึกไว้ไม่ได้ (${readErr}) จึงใช้ค่าเริ่มต้น`, true);
    else if (raw != null) {
      const r = parseSettings(raw);
      set = r.settings; remember = true;
      if (r.problem) toast(r.problem, true);
    }
  }
  let format: Choice = set.format;
  let busy = false;
  /** what the typed boxes say: a value, or why it cannot be used (the button waits) */
  let multErr = '', limitErr = '';

  /** keep the settings when "บันทึก" is on; a browser that refuses says so */
  function store() {
    if (!remember) return;
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...set, format })); }
    catch (e) { toast(`บันทึกการตั้งค่าการดาวน์โหลดไม่ได้ (${errText(e)}) ค่าที่ตั้งจะใช้ได้จนปิดหน้านี้เท่านั้น`, true); }
  }
  ($('exRemember') as HTMLInputElement).checked = remember;
  $('exRemember').onchange = e => {
    remember = (e.target as HTMLInputElement).checked;
    if (remember) { store(); return; }
    try { localStorage.removeItem(SETTINGS_KEY); }
    catch (err) { toast(`ลบการตั้งค่าที่บันทึกไว้ไม่ได้ (${errText(err)})`, true); }
  };

  const isPicture = () => format === 'png' || format === 'jpg';
  const quality = () => format === 'jpg' ? set.jpgQuality : set.pngQuality;
  const pictureOptions = (): PictureOptions => ({
    mult: set.mult, quality: quality(), limitBytes: set.limitKb * 1024, transparent: format === 'png' && set.transparent
  });

  const multBox = $('exMult') as HTMLInputElement, multText = $('exMultN') as HTMLInputElement;
  const limitBox = $('exLimit') as HTMLInputElement, transp = $('exTransp') as HTMLInputElement;
  multBox.value = String(set.mult); multText.value = String(set.mult);
  limitBox.value = String(set.limitKb); transp.checked = set.transparent;

  multBox.oninput = () => { set.mult = Number(multBox.value); multText.value = multBox.value; multErr = ''; store(); syncPicture(); syncGo(); };
  multText.oninput = () => {
    const r = checkMult(multText.value);
    multErr = r.ok ? '' : r.error;
    if (r.ok) { set.mult = r.value; multBox.value = String(r.value); store(); }
    syncPicture(); syncGo();
  };
  limitBox.oninput = () => {
    const r = checkLimitKb(limitBox.value);
    limitErr = r.ok ? '' : r.error;
    if (r.ok) { set.limitKb = r.value; store(); }
    syncPicture(); syncGo();
  };
  transp.onchange = () => { set.transparent = transp.checked; store(); syncPicture(); };
  $('exQuality').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b || busy) return;
    if (format === 'jpg') set.jpgQuality = b.dataset.v as ExportSettings['jpgQuality'];
    else set.pngQuality = b.dataset.v as ExportSettings['pngQuality'];
    store(); syncPicture(); syncGo();
  };

  /** the PNG/JPG part of the sheet: size in px (red when an iPad cannot draw it), quality, transparency */
  function syncPicture() {
    $('exPic').hidden = !isPicture();
    if (!isPicture()) return;
    const { w, h } = ed.doc;
    const p = sizePlan(w, h, set.mult);
    const px = $('exPx');
    px.textContent = multErr ? '' : `· ${p.outW.toLocaleString('en-US')} × ${p.outH.toLocaleString('en-US')} px`;
    px.classList.toggle('err-text', p.reduced);
    const shown = multErr || (p.reduced
      ? `ขอ ${p.wantW.toLocaleString('en-US')} × ${p.wantH.toLocaleString('en-US')} px แต่ iPad/iPhone วาดภาพใหญ่กว่า 16.7 ล้านพิกเซลไม่ได้ จะได้ ${p.outW.toLocaleString('en-US')} × ${p.outH.toLocaleString('en-US')} px ถ้าต้องการความละเอียดเต็มให้ใช้ PDF`
      : '');
    $('exMultErr').hidden = !shown; $('exMultErr').textContent = shown;
    // quality choices of this file type
    const labels: Record<string, string> = format === 'jpg' ? JPG_QUALITY_LABEL : PNG_QUALITY_LABEL;
    const q = $('exQuality');
    if (q.dataset.for !== format) {
      q.dataset.for = format; q.textContent = '';
      for (const [v, label] of Object.entries(labels)) { const b = el('button', '', label); b.dataset.v = v; q.append(b); }
    }
    setPressed('exQuality', b => b.dataset.v === quality());
    $('exLimitRow').hidden = quality() !== 'limit';
    $('exLimitErr').hidden = quality() !== 'limit' || !limitErr; $('exLimitErr').textContent = limitErr;
    $('exTranspRow').hidden = format !== 'png';
    $('exTranspNote').hidden = !(format === 'jpg' && set.transparent);
  }

  let profile: PrintProfileInfo | null = null;
  let profileErr = '';
  let profileMissing = false;
  const go = $('exGo') as HTMLButtonElement, status = $('exStatus');

  const bleed = () => ed.doc.bleedMm ?? 0;   // the open page's
  /**
   * what the exporters start from: name and background, and page 1's size and bleed — each page's own size and bleed
   * come from the design JSON (src/editor/pageFormat.ts pageDocs; หน้าหลายขนาด)
   */
  const base = () => { const one = history.pageSize(0); return { ...ed.doc, ...one, bleedMm: one.bleedMm ?? 0 }; };
  const mmOf = (px: number) => Math.round(pxToMm(px) * 10) / 10;
  /** "หน้า 1 210 × 297 mm ตัดตก 3 mm · หน้า 2 …" when the pages differ */
  const pageList = (withBleed: boolean) => history.pageSizes().map((s, k) =>
    `หน้า ${k + 1} ${mmOf(s.w)} × ${mmOf(s.h)} mm${withBleed ? ((s.bleedMm ?? 0) > 0 ? ` ตัดตก ${s.bleedMm} mm` : ' ไม่มีตัดตก') : ''}`).join(' · ');

  function info() {
    const { w, h } = ed.doc;
    if (isVector(format)) return '';
    if (format === 'pdf') {
      const mmW = Math.round(w / 300 * 25.4), mmH = Math.round(h / 300 * 25.4);
      const size = history.mixed() ? `แต่ละหน้าตามขนาดของหน้านั้น (${pageList(false)})` : `ขนาดหน้า ${mmW} × ${mmH} mm`;
      return `PDF แบบภาพ 300 DPI สี RGB ${size} ทั้งหน้าเป็นรูป คัดลอกตัวหนังสือไม่ได้ ถ้าส่งเอกสารให้ใช้ "PDF เอกสาร" (ไฟล์เล็กกว่า) ถ้าส่งโรงพิมพ์ให้ใช้ "PDF โรงพิมพ์"`;
    }
    // the size itself is shown above the slider (syncPicture)
    const page = history.mixed() ? `ขนาดเป็นของหน้า ${history.currentPage() + 1} แต่ละหน้าได้ไฟล์ตามขนาดของหน้านั้น คูณด้วยตัวคูณเดียวกัน · ` : '';
    return `${page}งานจริง ${w} × ${h} px${format === 'jpg' ? ' · JPG ไฟล์เล็กกว่า PNG เหมาะกับรูปถ่าย' : ' · PNG คมชัด เหมาะกับโพสต์ที่มีตัวหนังสือ'}`;
  }

  /* ---------- vector PDFs: what will be made, and the check list ---------- */
  function paintPrintInfo() {
    const box = $('exPrintInfo');
    box.textContent = '';
    const mm = mmOf, mixed = history.mixed();
    if (format === 'doc') {
      box.append(mixed ? `แต่ละหน้าพอดีขนาดของหน้านั้น: ${pageList(false)} · ไม่มีเส้นตัด · สี RGB ตามจอ` : `หน้า ${mm(ed.doc.w)} × ${mm(ed.doc.h)} mm พอดีขนาดงาน · ไม่มีเส้นตัด · สี RGB ตามจอ`, el('br'),
        'ตัวหนังสือไทยคมชัดทุกขนาด และ', el('b', '', 'คัดลอก/ค้นหาได้'), el('br'),
        `รูปย่อเหลือไม่เกิน ${DOC_PPI} ppi ไฟล์เล็ก เหมาะกับใบเสนอราคา ใบแจ้งหนี้ ส่งทางอีเมลหรือไลน์ และพิมพ์ด้วยเครื่องพิมพ์สำนักงาน`);
      return;
    }
    box.append(mixed ? `ขนาดตัดจริงแต่ละหน้า: ${pageList(true)} · มีเส้นตัด`
      : `ขนาดตัดจริง ${mm(ed.doc.w)} × ${mm(ed.doc.h)} mm · ${bleed() > 0 ? `ตัดตก ${bleed()} mm ทุกด้าน` : 'ไม่มีตัดตก'} · มีเส้นตัด`, el('br'));
    if (profile) {
      box.append('โปรไฟล์สี ', el('b', '', profile.name), ` · ${profile.builtIn ? 'ค่าเริ่มต้น' : 'ตั้งโดยผู้ดูแล'}`);
      if (profileMissing) box.append(el('br'), 'โปรไฟล์ที่ผู้ดูแลเลือกไว้ถูกลบไปแล้ว จึงใช้ค่าเริ่มต้นแทน');
    } else box.append(profileErr ? `โปรไฟล์สี: ${profileErr}` : 'กำลังโหลดโปรไฟล์สี…');
    box.append(el('br'), 'ตัวหนังสือแปลงเป็นเส้น โรงพิมพ์ไม่ต้องมีฟอนต์ · ไฟล์ .pdf');
  }

  /** each page's objects */
  // โน้ตแปะ are not in the file, so they are not checked either
  const design = () => history.allPageObjects<PlainObject>().map(p => p.filter(o => (o as { note?: unknown }).note !== true));

  function paintChecks() {
    const list = $('exChecks');
    list.textContent = '';
    let checks: Check[];
    try {
      const pages = design(), sizes = history.pageSizes();   // each page at its own size and bleed
      // only the pages being exported (เลือกหน้า); page numbers stay the design's own
      const only = chosen();
      const pick = only.ok && only.pages.length < pages.length ? only.pages : undefined;
      checks = format === 'doc' ? preflightDoc(pages, sizes, pick) : preflight(pages, sizes, pick);
    }
    catch (e) { list.append(el('p', 'admsg', 'ตรวจงานไม่สำเร็จ: ' + errText(e))); return; }
    const editable = opts.canEdit();
    for (const c of checks) {
      const row = el('div', 'pfitem');
      row.append(el('span', `pfdot ${c.kind}`, c.kind === 'warn' ? '!' : c.kind === 'info' ? 'i' : '✓'));
      const body = el('div');
      body.append(el('b', '', c.title), el('span', '', c.text));
      if (c.action && editable) {
        const b = el('button', 'pfact', c.action.label);
        const action = c.action;
        b.onclick = () => fix(action);
        body.append(b);
      }
      row.append(body);
      list.append(row);
    }
  }

  /**
   * The buttons in the check list change the design (and are undoable like any edit).
   * A problem on another page opens that page first.
   */
  function fix(a: NonNullable<Check['action']>) {
    if (a.kind === 'bleed') { opts.setBleed(a.mm, a.pages); paintPrintInfo(); paintChecks(); return; }
    if (a.kind === 'black') {
      const n = history.setOnPages(a.items, { fill: '#000000' });
      if (n) toast(`เปลี่ยนตัวหนังสือ ${n} กล่องเป็นดำล้วนแล้ว (เลิกทำได้)`);
      else toast('ไม่พบตัวหนังสือเหล่านี้แล้ว (งานเปลี่ยนไป) ตรวจใหม่ให้แล้ว', true);
      paintChecks(); return;
    }
    const why = history.goPage(a.page, () => fixOnPage(a));
    if (why) toast(why, true);
  }

  function fixOnPage(a: Extract<NonNullable<Check['action']>, { kind: 'select' | 'extend' }>) {
    const objs = ed.userObjects();
    if (a.kind === 'select') {
      const o = objs[a.index];
      if (!o) { toast('ไม่พบชิ้นนี้แล้ว (งานเปลี่ยนไป) ตรวจใหม่ให้แล้ว', true); paintChecks(); return; }
      if (o.selectable === false) { toast('ชิ้นนี้ถูกล็อกอยู่ ปลดล็อกได้ที่แท็บเลเยอร์', true); return; }
      closeSheets(); ed.canvas.setActiveObject(o); ed.canvas.requestRenderAll();
      return;
    }
    // extend a plain rectangle out to the bleed on the sides that stop short
    const o = objs[a.index] as fabric.Rect | undefined;
    if (!o || o.type !== 'rect') { toast('ไม่พบชิ้นนี้แล้ว (งานเปลี่ยนไป) ตรวจใหม่ให้แล้ว', true); paintChecks(); return; }
    const bl = mmToPx(bleed()), tol = mmToPx(1);
    const b = objectBounds(o.toObject() as PlainObject);
    const T = { x0: b.x, y0: b.y, x1: b.x + b.w, y1: b.y + b.h };
    if (b.x <= tol) T.x0 = -bl;
    if (b.y <= tol) T.y0 = -bl;
    if (b.x + b.w >= ed.doc.w - tol) T.x1 = ed.doc.w + bl;
    if (b.y + b.h >= ed.doc.h - tol) T.y1 = ed.doc.h + bl;
    const sx = Math.abs(o.scaleX ?? 1), sy = Math.abs(o.scaleY ?? 1);
    o.set({ width: (o.width ?? 0) + (T.x1 - T.x0 - b.w) / sx, height: (o.height ?? 0) + (T.y1 - T.y0 - b.h) / sy });
    o.setCoords();
    const after = objectBounds(o.toObject() as PlainObject);
    o.set({ left: (o.left ?? 0) + T.x0 - after.x, top: (o.top ?? 0) + T.y0 - after.y });
    o.setCoords(); ed.canvas.requestRenderAll(); history.push();
    toast('ขยายถึงขอบตัดตกแล้ว (เลิกทำได้)');
    paintChecks();
  }

  let profileRun = 0;
  async function loadProfile() {
    const run = ++profileRun;
    profile = null; profileErr = ''; paintPrintInfo(); syncGo();
    try {
      const r = await api.printProfile();
      if (run !== profileRun) return;
      profile = r.profile; profileMissing = r.missing;
    } catch (e) {
      if (run !== profileRun) return;
      profileErr = `โหลดไม่ได้ (${errText(e)}) จึงยังสร้าง PDF โรงพิมพ์ไม่ได้ ลองใหม่เมื่อออนไลน์`;
    }
    paintPrintInfo(); syncGo();
  }

  /* ---------- เลือกหน้า: ทั้งหมด / หน้านี้ / ตั้งค่าเอง (not remembered: back to ทั้งหมด each time the sheet opens) ---------- */
  let pagesMode: PagesMode = 'all';
  /** ตั้งค่าเอง: the ticked pages (0-based), and why the typed range cannot be used ('' = it can) */
  let picked = new Set<number>();
  let rangeErr = '';
  const rangeBox = $('exRange') as HTMLInputElement;

  /** the pages to export, in page order — or why there are none */
  function chosen(): { ok: true; pages: number[] } | { ok: false; error: string } {
    const n = history.pageCount();
    if (pagesMode === 'all' || n <= 1) return { ok: true, pages: Array.from({ length: n }, (_, k) => k) };
    if (pagesMode === 'this') return { ok: true, pages: [history.currentPage()] };
    if (rangeErr) return { ok: false, error: rangeErr };
    // the design may have changed while the sheet was open (a page deleted): checked again here
    return parseRange(formatRange([...picked]), n);
  }
  /** files this download makes */
  function fileCount(): number {
    const c = chosen();
    if (!c.ok) return 0;
    return isPicture() || (c.pages.length > 1 && set.splitPdf) ? c.pages.length : 1;
  }

  function syncGo() {
    const files = fileCount(), c = chosen();
    go.disabled = busy || !c.ok || (format === 'print' && !profile) || (isPicture() && (!!multErr || (quality() === 'limit' && !!limitErr)));
    const what = format === 'print' ? ' PDF โรงพิมพ์' : format === 'doc' ? ' PDF เอกสาร' : '';
    go.textContent = files > 1 ? `ดาวน์โหลด${what} ${files} ไฟล์${set.zip ? ' (ZIP)' : ''}` : `ดาวน์โหลด${what}`;
  }

  function syncPages() {
    const n = history.pageCount();
    $('exPagesBox').hidden = n <= 1;
    if (n <= 1) return;
    setPressed('exPages', b => b.dataset.v === pagesMode);
    $('exPageAll').textContent = `ทั้งหมด (${n})`;
    $('exPageThis').textContent = `หน้านี้ (${history.currentPage() + 1})`;
    $('exPick').hidden = pagesMode !== 'pick';
    $('exRangeErr').hidden = !rangeErr; $('exRangeErr').textContent = rangeErr;
    if (pagesMode === 'pick') paintGrid();
    const c = chosen(), m = c.ok ? c.pages.length : 0;
    const pdf = !isPicture();
    $('exPagesNote').textContent = !c.ok ? '' : isPicture() ? `ได้ไฟล์ละหน้า ${m} ไฟล์`
      : m > 1 && set.splitPdf ? `PDF หน้าละไฟล์ ${m} ไฟล์` : `PDF ไฟล์เดียว ${m} หน้า เรียงตามลำดับหน้า`;
    $('exSplitRow').hidden = !(pdf && m > 1);
    ($('exSplit') as HTMLInputElement).checked = set.splitPdf;
    $('exZipRow').hidden = fileCount() <= 1;
    ($('exZip') as HTMLInputElement).checked = set.zip;
  }

  $('exPages').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b || busy) return;
    pagesMode = b.dataset.v as PagesMode;
    if (pagesMode === 'pick' && !picked.size && !rangeErr) { picked = new Set([history.currentPage()]); rangeBox.value = formatRange([...picked]); }
    pagesChanged();
  };
  rangeBox.oninput = () => {
    const r = parseRange(rangeBox.value, history.pageCount());
    rangeErr = r.ok ? '' : r.error;
    if (r.ok) picked = new Set(r.pages);
    pagesChanged();
  };
  $('exPickAll').onclick = () => { picked = new Set(Array.from({ length: history.pageCount() }, (_, k) => k)); fromTicks(); };
  $('exPickNone').onclick = () => { picked = new Set(); fromTicks(); };
  $('exSplit').onchange = e => { set.splitPdf = (e.target as HTMLInputElement).checked; store(); pagesChanged(); };
  $('exZip').onchange = e => { set.zip = (e.target as HTMLInputElement).checked; store(); syncGo(); };
  /** the ticks changed: the typed box follows them */
  function fromTicks() {
    rangeBox.value = formatRange([...picked]);
    const r = parseRange(rangeBox.value, history.pageCount());
    rangeErr = r.ok ? '' : r.error;
    pagesChanged();
  }
  function pagesChanged() {
    syncPages();
    if (isVector(format)) paintChecks();
    syncGo();
  }

  /* the previews to tick: drawn when seen, from the page strip's own cache (src/ui/pageThumbs.ts) */
  const grid = $('exGrid');
  let gridFor = '';
  const seenInGrid = new IntersectionObserver(entries => {
    for (const e of entries) if (e.isIntersecting) { seenInGrid.unobserve(e.target); drawGridThumb(e.target as HTMLElement); }
  }, { root: grid, rootMargin: '120px 0px' });
  function paintGrid() {
    const n = history.pageCount();
    const sig = `${n}|${Array.from({ length: n }, (_, k) => history.pageName(k)).join('\u0001')}`;
    if (sig !== gridFor) {
      gridFor = sig; seenInGrid.disconnect(); grid.textContent = '';
      for (let k = 0; k < n; k++) {
        const b = el('button', 'expgitem');
        b.dataset.k = String(k); b.setAttribute('role', 'listitem');
        const name = history.pageName(k);
        b.setAttribute('aria-label', `หน้า ${k + 1}${name ? ' ' + name : ''}`);
        const cap = el('span', 'expgcap');
        cap.append(el('span', 'expgtick'), el('b', '', String(k + 1)), el('span', '', name));
        b.append(el('span', 'expgpic'), cap);
        b.onclick = () => { if (picked.has(k)) picked.delete(k); else picked.add(k); fromTicks(); };
        grid.append(b);
        seenInGrid.observe(b);
      }
    }
    grid.querySelectorAll<HTMLElement>('.expgitem').forEach(b => {
      const on = picked.has(Number(b.dataset.k));
      b.setAttribute('aria-pressed', String(on));
      b.querySelector('.expgtick')!.textContent = on ? '✓' : '';
    });
  }
  function drawGridThumb(item: HTMLElement) {
    const k = Number(item.dataset.k), pic = item.querySelector<HTMLElement>('.expgpic')!;
    if (!(k < history.pageCount())) return;
    const json = history.pageJson(k), sz = history.pageSize(k);
    const show = (url: string) => { pic.style.backgroundImage = `url("${url}")`; pic.textContent = ''; };
    const ready = readyThumb(json, sz, 200);
    if (ready) { show(ready); return; }
    pageThumb(json, sz, ed.doc, 200).then(
      url => { if (pic.isConnected) show(url); },
      e => { if (pic.isConnected) { pic.textContent = '!'; pic.title = 'แสดงตัวอย่างไม่ได้: ' + errText(e); } }
    );
  }

  /** opening the sheet: the page choice starts again from ทั้งหมด (5ก) */
  function opened() {
    pagesMode = 'all'; picked = new Set(); rangeErr = ''; rangeBox.value = ''; gridFor = '';
    sync();
  }

  function sync() {
    setPressed('exFormat', b => b.dataset.v === format);
    $('exInfo').textContent = info();
    $('exInfo').hidden = isVector(format);
    // โน้ตแปะ are left out of every file: say so, so a missing note is never a surprise
    const notes = noteCount(history.snapshot());
    $('exNotes').hidden = !notes;
    $('exNotes').textContent = `มีโน้ตแปะ ${notes} ชิ้น โน้ตแสดงบนจอเท่านั้น จะไม่อยู่ในไฟล์นี้`;
    $('exPrint').hidden = !isVector(format);
    if (isVector(format)) { paintPrintInfo(); paintChecks(); }
    // the profile is asked for every time: an admin may have changed it since this page was opened
    if (format === 'print') void loadProfile();
    syncPages();
    syncPicture();
    syncGo();
  }

  $('exFormat').onclick = e => {
    const b = (e.target as Element).closest('button'); if (!b || busy) return;
    format = b.dataset.v as Choice; status.textContent = ''; store(); sync();
  };

  async function makePrint(json: string): Promise<{ blob: Blob; note?: string }> {
    status.textContent = 'กำลังตรวจโปรไฟล์สีล่าสุด…';
    await loadProfile();
    if (!profile) throw new Error(profileErr || 'ยังไม่ได้โหลดโปรไฟล์สี');
    status.textContent = 'กำลังโหลดเครื่องมือสร้าง PDF โรงพิมพ์…';
    const [{ buildPrintPdf }, wasm, icc] = await Promise.all([
      import('../print/pdf'), import('lcms-wasm/dist/lcms.wasm?url'), iccOf(profile)
    ]).catch(e => { throw new Error('โหลดเครื่องมือสร้าง PDF ไม่สำเร็จ: ' + errText(e)); });
    const r = await buildPrintPdf(json, base(),
      { id: profile.id, name: profile.name, condition: profile.condition, registry: profile.registry, icc }, wasm.default,
      t => { status.textContent = t; });
    return { blob: r.blob, note: r.notes.length ? r.notes.join(' · ') : undefined };
  }

  async function makeDoc(json: string): Promise<{ blob: Blob; note?: string }> {
    status.textContent = 'กำลังโหลดเครื่องมือสร้าง PDF เอกสาร…';
    const { buildDocPdf } = await import('../print/pdf').catch(e => { throw new Error('โหลดเครื่องมือสร้าง PDF ไม่สำเร็จ: ' + errText(e)); });
    const r = await buildDocPdf(json, base(), t => { status.textContent = t; });
    return { blob: r.blob, note: r.notes.length ? r.notes.join(' · ') : undefined };
  }

  /** " หน้า3" or " หน้า3 ชื่อหน้า" (ตั้งชื่อหน้า) in a file name; the number keeps the files in page order */
  const pageTag = (k: number) => { const n = history.pageName(k); return `หน้า${k + 1}${n ? ' ' + n : ''}`; };

  /** a ZIP bigger than this is refused with the reason (an iPad holds it all in memory) */
  const ZIP_MAX_BYTES = 400 * 1024 * 1024;

  /**
   * Makes the files one at a time and hands them over: one by one as each is made, or all in one ZIP at the end.
   * Stops at the first file that fails and says which (and what was already downloaded).
   */
  async function makeFiles(jobs: Array<{ label: string; name: string; make: () => Promise<ExportResult> }>, zipName: string) {
    const zip = set.zip && jobs.length > 1;
    const made: Array<{ name: string; blob: Blob }> = [];
    const notes: string[] = [];
    let total = 0;
    for (const [i, j] of jobs.entries()) {
      const where = jobs.length > 1 ? `${j.label} (${i + 1} จาก ${jobs.length}): ` : '';
      let r: ExportResult;
      try { r = await j.make(); }
      catch (e) {
        throw new Error(`${where}${errText(e)}${!zip && i ? ` (ดาวน์โหลดไปแล้ว ${i} ไฟล์)` : ''}`);
      }
      total += r.blob.size;
      if (r.note) notes.push(jobs.length > 1 ? `${j.label}: ${r.note}` : r.note);
      if (zip) {
        if (total > ZIP_MAX_BYTES) throw new Error(`ไฟล์รวมกันเกิน ${size(ZIP_MAX_BYTES)} ใหญ่เกินกว่าจะรวมเป็น ZIP ในเครื่องได้ ปิด "รวมเป็นไฟล์ ZIP" แล้วดาวน์โหลดทีละไฟล์ หรือเลือกหน้าน้อยลง`);
        made.push({ name: j.name, blob: r.blob });
      } else {
        download(r.blob, j.name);
        if (jobs.length > 1) await new Promise(res => setTimeout(res, 500));   // browsers drop downloads fired all at once
      }
      if (where) status.textContent = `${where}เสร็จแล้ว`;
    }
    const tail = notes.length ? ' · ' + notes.join(' · ') : '';
    if (zip) {
      status.textContent = 'กำลังรวมเป็นไฟล์ ZIP…';
      const blob = await zipFiles(made);
      download(blob, zipName);
      status.textContent = `ดาวน์โหลด "${zipName}" แล้ว (${made.length} ไฟล์ ${size(blob.size)})${tail}`;
    } else if (jobs.length > 1) {
      status.textContent = `ดาวน์โหลด ${jobs.length} ไฟล์แล้ว (รวม ${size(total)}) ถ้าเบราว์เซอร์ถามว่าให้ดาวน์โหลดหลายไฟล์ไหม ให้กดอนุญาต${tail}`;
    } else {
      status.textContent = `ดาวน์โหลด "${jobs[0].name}" แล้ว (${size(total)})${tail}`;
    }
    toast(notes.length && isVector(format) ? 'สร้างไฟล์เสร็จแล้ว มีบางชิ้นเป็นภาพ ดูรายละเอียดในหน้าส่งออก'
      : jobs.length > 1 ? `สร้างไฟล์ ${jobs.length} ไฟล์เสร็จแล้ว` : 'สร้างไฟล์เสร็จแล้ว');
  }

  go.onclick = async () => {
    if (busy) return;
    busy = true; syncGo();
    try {
      ed.canvas.discardActiveObject();
      const json = history.snapshot();
      if (isPicture() && (multErr || (quality() === 'limit' && limitErr))) throw new Error(multErr || limitErr);
      const c = chosen();
      if (!c.ok) throw new Error(c.error);
      const pages = c.pages, n = history.pageCount(), stamp = thStamp(new Date()), ext = EXT[format];
      const some = pages.length < n;
      const zipName = exportFileName(ed.doc.name, stamp, 'zip', some ? `หน้า ${formatRange(pages)}` : '');
      const fallback = history.pageSize(0);
      const progress = (label: string) => (t: string) => { status.textContent = pages.length > 1 ? `${label}: ${t}` : t; };
      const pdfOf = (sub: string, label: string) => format === 'print' ? makePrint(sub)
        : format === 'doc' ? makeDoc(sub)
        : exportDesign(sub, base(), 'pdf', progress(label));
      let jobs: Array<{ label: string; name: string; make: () => Promise<ExportResult> }>;
      if (isPicture()) {
        jobs = pages.map(k => ({
          label: `หน้า ${k + 1}`, name: exportFileName(ed.doc.name, stamp, ext, n > 1 ? pageTag(k) : ''),
          make: () => exportDesign(json, base(), format as Format, progress(`หน้า ${k + 1}`), k, pictureOptions())
        }));
      } else if (pages.length > 1 && set.splitPdf) {
        jobs = pages.map(k => ({
          label: `หน้า ${k + 1}`, name: exportFileName(ed.doc.name, stamp, ext, pageTag(k)),
          make: () => pdfOf(pickPages(json, [k], fallback), `หน้า ${k + 1}`)
        }));
      } else {
        // one PDF of the chosen pages, in page order (the whole design when every page is chosen)
        jobs = [{
          label: 'PDF', name: exportFileName(ed.doc.name, stamp, ext, some ? `หน้า ${formatRange(pages)}` : ''),
          make: () => pdfOf(some ? pickPages(json, pages, fallback) : json, 'PDF')
        }];
      }
      await makeFiles(jobs, zipName);
    } catch (e) {
      status.textContent = 'ส่งออกไม่สำเร็จ: ' + errText(e);
      toast('ส่งออกไม่สำเร็จ: ' + errText(e), true);
    } finally {
      busy = false; syncGo();
    }
  };

  return { sync: opened };
}

/** files into one ZIP, stored as they are (PNG/JPG/PDF are compressed already); fflate loads only when used */
async function zipFiles(files: Array<{ name: string; blob: Blob }>): Promise<Blob> {
  const { zipSync } = await import('fflate').catch(e => { throw new Error('โหลดเครื่องมือทำไฟล์ ZIP ไม่สำเร็จ: ' + errText(e)); });
  const names = uniqueNames(files.map(f => f.name));
  const entries: Record<string, [Uint8Array, { level: 0 }]> = {};
  for (const [i, f] of files.entries()) entries[names[i]] = [new Uint8Array(await f.blob.arrayBuffer()), { level: 0 }];
  try { return new Blob([zipSync(entries) as Uint8Array<ArrayBuffer>], { type: 'application/zip' }); }
  catch (e) { throw new Error('รวมไฟล์เป็น ZIP ไม่สำเร็จ (หน่วยความจำไม่พอ?): ' + errText(e) + ' ลองปิด "รวมเป็นไฟล์ ZIP" แล้วดาวน์โหลดทีละไฟล์'); }
}
